import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, X } from 'lucide-react';

const defaultTriggerClass =
  'flex w-full min-h-[2.75rem] items-center justify-between gap-2 px-3 py-2.5 text-left text-sm leading-snug bg-surface-muted border border-edge rounded-lg text-content outline-none transition-colors hover:border-accent/50 focus:border-accent/50 focus:ring-1 focus:ring-accent/50 disabled:cursor-not-allowed disabled:opacity-50';

/** Appended to listbox; `-sm` = 4px thumb. `data-scrollbar-hover` fixes WebKit thumb visibility when hovering list content (see tailwind-scrollbar-aimsos-plugin). */
const LISTBOX_SCROLLBAR_CLASS = 'scrollbar-aimsos-auto-sm';

/** `max-h-48`, in px — the portaled listbox needs the number, not the class. */
const LISTBOX_MAX_HEIGHT_PX = 192;
/** `mt-1`. */
const LISTBOX_GAP_PX = 4;
/** Breathing room against the viewport edge. */
const LISTBOX_VIEWPORT_PAD_PX = 8;

/** Looks, without the positioning the portaled variant sets inline. */
const LISTBOX_SURFACE_CLASS =
  `overflow-y-auto ${LISTBOX_SCROLLBAR_CLASS} rounded-lg border border-edge bg-surface py-1 shadow-lg`;

/**
 * Multi-select styled like an input; opens downward with a scrollable checkbox list.
 *
 * @param {string[]} value - Array of selected option values.
 * @param {(values: string[]) => void} onChange - Called with the full updated selection array.
 * @param {{ value: string; label: string }[]} options
 * @param {string} [placeholder] - Text shown when nothing is selected.
 * @param {(selected: { value: string; label: string }[]) => string} [renderValue]
 *   Custom function to render the trigger label when items are selected.
 *   Receives the array of selected option objects; defaults to joining labels with ", ".
 * @param {'start'|'end'} [align='start'] - Which edge of the trigger the dropdown anchors to.
 * @param {string} [triggerClassName] - Extra classes merged onto the trigger button.
 * @param {string} [listboxClassName] - Replaces default listbox classes; `scrollbar-aimsos-auto-sm` is always appended for vertical scroll styling.
 * @param {boolean} [portalListbox=false] - Render the list into `document.body` as a
 *   fixed-position layer instead of absolutely inside the trigger's box.
 *
 *   Same reasoning as `FormSelect`'s prop of the same name: turn it on when the
 *   select sits inside anything that clips — a scrolling panel, a widget card,
 *   an `overflow-hidden` container. An absolutely positioned list is a child of
 *   that box and gets cut off at its edge, and no `z-index` fixes that, because
 *   clipping is not a stacking problem (CT-5887).
 *
 *   Opt-in rather than the default so a caller passing its own
 *   `listboxClassName` for absolute positioning is not quietly overruled by the
 *   inline `position: fixed`.
 */
const FormMultiSelect = ({
  id,
  value = [],
  onChange,
  options = [],
  placeholder,
  disabled = false,
  renderValue,
  triggerClassName,
  listboxClassName,
  align = 'start',
  portalListbox = false,
}) => {
  const [open, setOpen] = useState(false);
  const [listboxScrollHover, setListboxScrollHover] = useState(false);
  const [listboxStyle, setListboxStyle] = useState(null);
  const containerRef = useRef(null);
  const listboxRef = useRef(null);
  const listboxId = useId();

  const mergedTriggerClass = triggerClassName
    ? `${defaultTriggerClass} ${triggerClassName}`
    : defaultTriggerClass;

  const updateListboxPosition = useCallback(() => {
    const trigger = containerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - LISTBOX_GAP_PX - LISTBOX_VIEWPORT_PAD_PX;
    const spaceAbove = rect.top - LISTBOX_GAP_PX - LISTBOX_VIEWPORT_PAD_PX;
    // Flip up only when down genuinely cannot hold a usable list AND up is
    // roomier — a select near the fold should not jump upward for one row.
    const openUp = spaceBelow < Math.min(LISTBOX_MAX_HEIGHT_PX, 120) && spaceAbove > spaceBelow;
    const maxHeight = Math.min(LISTBOX_MAX_HEIGHT_PX, Math.max(96, openUp ? spaceAbove : spaceBelow));
    const width = Math.min(rect.width, window.innerWidth - LISTBOX_VIEWPORT_PAD_PX * 2);
    const left = Math.min(
      Math.max(LISTBOX_VIEWPORT_PAD_PX, align === 'end' ? rect.right - width : rect.left),
      window.innerWidth - width - LISTBOX_VIEWPORT_PAD_PX,
    );

    setListboxStyle({
      position: 'fixed',
      top: openUp ? 'auto' : rect.bottom + LISTBOX_GAP_PX,
      bottom: openUp ? window.innerHeight - rect.top + LISTBOX_GAP_PX : 'auto',
      left,
      minWidth: width,
      maxHeight,
      zIndex: 1000,
    });
  }, [align]);

  // Follows the trigger while anything scrolls — `capture` because the
  // scroller is usually an ancestor panel, not the window.
  useLayoutEffect(() => {
    if (!open || !portalListbox) return undefined;
    updateListboxPosition();
    const raf = requestAnimationFrame(updateListboxPosition);
    window.addEventListener('resize', updateListboxPosition);
    window.addEventListener('scroll', updateListboxPosition, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateListboxPosition);
      window.removeEventListener('scroll', updateListboxPosition, true);
    };
  }, [open, portalListbox, updateListboxPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      if (containerRef.current?.contains(e.target)) return;
      // The portaled list is outside the trigger's subtree, so without this
      // clicking an option would dismiss before the click landed.
      if (listboxRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setListboxScrollHover(false);
  }, [open]);

  const handleListboxMouseEnter = useCallback(() => setListboxScrollHover(true), []);
  const handleListboxMouseLeave = useCallback(() => setListboxScrollHover(false), []);

  const handleToggle = useCallback(
    (optValue) => {
      const next = value.includes(optValue)
        ? value.filter((v) => v !== optValue)
        : [...value, optValue];
      onChange?.(next);
    },
    [value, onChange],
  );

  const handleClearAll = useCallback(
    (e) => {
      e.stopPropagation();
      onChange?.([]);
    },
    [onChange],
  );

  const selectedOptions = options.filter((o) => value.includes(o.value));
  const hasSelection = selectedOptions.length > 0;

  const triggerLabel = hasSelection
    ? renderValue
      ? renderValue(selectedOptions)
      : selectedOptions.map((o) => o.label).join(', ')
    : placeholder;

  const listbox = (
    <ul
      ref={listboxRef}
      id={listboxId}
      role="listbox"
      aria-multiselectable="true"
      aria-labelledby={id}
      data-scrollbar-hover={listboxScrollHover ? 'true' : undefined}
      onMouseEnter={handleListboxMouseEnter}
      onMouseLeave={handleListboxMouseLeave}
      style={portalListbox ? (listboxStyle ?? { position: 'fixed', visibility: 'hidden' }) : undefined}
      className={
        portalListbox
          ? `w-max max-w-xs ${LISTBOX_SURFACE_CLASS}`
          : listboxClassName
            ? `${listboxClassName} ${LISTBOX_SCROLLBAR_CLASS}`
            : `absolute top-full z-[100] mt-1 min-w-full w-max max-w-xs max-h-48 ${LISTBOX_SURFACE_CLASS} ${align === 'end' ? 'right-0' : 'left-0'}`
      }
    >
      {options.map((opt) => {
        const isSelected = value.includes(opt.value);
        return (
          <li
            key={opt.value}
            role="option"
            aria-selected={isSelected}
            className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm transition-colors hover:bg-surface-hover"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleToggle(opt.value)}
          >
            <span
              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors ${
                isSelected
                  ? 'border-accent bg-accent'
                  : 'border-edge bg-transparent'
              }`}
              aria-hidden
            >
              {isSelected ? <Check className="h-3 w-3 text-white" strokeWidth={3} /> : null}
            </span>
            <span className="truncate text-content">{opt.label}</span>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        className={mergedTriggerClass}
        onClick={() => {
          if (!disabled) setOpen((o) => !o);
        }}
      >
        <span
          className={`min-w-0 flex-1 truncate ${hasSelection ? 'text-content' : 'text-content-muted'}`}
        >
          {triggerLabel}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {hasSelection ? (
            <span
              role="button"
              tabIndex={0}
              aria-label="Clear selection"
              className="flex h-4 w-4 items-center justify-center rounded text-content-muted hover:text-content"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleClearAll}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleClearAll(e);
              }}
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </span>
          ) : null}
          <ChevronDown
            className={`h-4 w-4 text-content-muted transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </span>
      </button>

      {open ? (portalListbox ? createPortal(listbox, document.body) : listbox) : null}
    </div>
  );
};

export default FormMultiSelect;
